import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../../../../../ui/icon/icon.component';
import { TranslatePipe } from '../../../../../../pipes/translate/translate.pipe';
import { DatetimePipe } from '../../../../../../pipes/dates/datetime.pipe';
import { TextAreaModule } from '@progress/kendo-angular-inputs';
import { CompareBaseModalComponent } from '../../compare-base-modal.component';
import { ExtensionIconComponent } from 'src/app/shared/components/ui/icon/extension-icon/extension-icon.component';
import { VersionType } from 'src/app/shared/enums/version-type';
import { take } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { CompareButtonComponent } from '../../components/compare-button/compare-button.component';
import { DownloadService } from 'src/app/core/services/download/download.service';


/**
 * Modal that offers the user the choice to download the draft or published and/or pdf
 * version of an instance.
 */
@Component({
	selector: "bizz-download-draft-published-or-pdf-modal",
	standalone: true,
	imports: [CommonModule, IconComponent, TranslatePipe, DatetimePipe, TextAreaModule, ExtensionIconComponent, CompareButtonComponent],
	templateUrl: "./download-draft-published-or-pdf-modal.component.html",
	styleUrls: ["../../compare-base-modal.component.scss"],
})
export class DownloadDraftPublishedOrPdfModal extends CompareBaseModalComponent {
	
	private buildDownloadUrl = (versionType: VersionType, docType: "file" | "pdffile", versionId: number): string => {
		if (!this.data.CollectionsID || !this.data.VersionsID) {
			throw new Error("CollectionsID, InstanceID are required to download a file");
		}

		return `api/collection/${this.data.CollectionsID}/instance/${this.data.ID}/version/${versionId}/${docType}`;
	};

	private buildFileName = (versionType: VersionType, docType: "file" | "pdffile"): string | undefined => {
		const model = versionType === VersionType.Draft ? this.model["DraftVersion"] : this.model["PublicVersion"];
		const { Title: title, Extension: extension } = model;

		if (!title || !title.Value || !extension || !extension.Value) {
			return undefined;
		}

		return `${title.Value}${docType === "pdffile" ? ".pdf" : extension.Value}`;
	};

	public download(versionType: VersionType, docType: "file" | "pdffile"): void {
		if (this.pwaService.isPwa() && !this.networkService.isOnline) {
      this.downloadOfflineFile(docType);
      this.dialogRef.close();
		}

		const version = this.getVersionIdByType(versionType);
		if (!version) {
			console.error("No version found for selected version type");
			return;
		}

		const url = this.buildDownloadUrl(versionType, docType, version);
		this.http
			.get(url, { observe: "response", responseType: "arraybuffer" })
			.pipe(take(1))
			.subscribe((response: HttpResponse<ArrayBuffer>) => {
				if (response.body != null && response.body.byteLength > 0) {
					const headers = response.headers;
					const fileNameFromHeader = headers.get("x-filename");
					const fileName = DownloadService.cleanupFileName(fileNameFromHeader);
					this.downloadService.startDownload(response.body, fileName);
				}
			});

		this.dialogRef.close();
	}

  public getFileExtension(versionType: VersionType): string | undefined {
    if (versionType === VersionType.Draft && typeof this.model['DraftVersion']['Extension']?.Value === 'string') {
      return this.model['DraftVersion']['Extension']?.Value;
    } else if (versionType === VersionType.Public && typeof this.model['PublicVersion']['Extension']?.Value === 'string') {
      return this.model['PublicVersion']['Extension']?.Value;
    } else if (versionType === VersionType.Prepublication && typeof this.model['PrepublicationVersion']['Extension']?.Value === 'string') {
      return this.model['PrepublicationVersion']['Extension']?.Value;
    } else if (versionType === VersionType.Archive && typeof this.model['ArchiveVersion']['Extension']?.Value === 'string') {
      return this.model['ArchiveVersion']['Extension']?.Value;
    }

		return;
	}
  
	public downloadOfflineFile(docType: "file" | "pdffile"): void {
		const data = this.modalData.comparison.PublicVersion as unknown as {
			PdfFile: { Value: string; File: string; ContentType: string };
			OriginalFile: { Value: string; File: string; ContentType: string };
		};
		if (docType === "file") {
			this.downloadService.downloadOfflineFile(data.OriginalFile.Value, data.OriginalFile.File, data.OriginalFile.ContentType);
		} else {
			this.downloadService.downloadOfflineFile(data.PdfFile.Value, data.PdfFile.File, data.PdfFile.ContentType);
		}
	}
}
